import { Typography, Divider, Flex } from 'antd'
import dayjs from 'dayjs'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { useGetMachinesQuery } from '../../../../../store/api/machines-api'
import formatNumber from '../../../../../helpers/formatters/format-any-number'
import formatDuration from '../../../../../helpers/date-time/format-duration'
import {
    filterPrintQueueEntriesByBrands,
    getPrintQueueEntryAreaM2,
    getPrintQueueEntryMinutes,
    PRINT_PLAN_MACHINE_CARDS,
} from '../../helpers/build-print-plan-date-groups'
import {
    buildDurstPrintDayGroups,
    getDurstPrintDayGroupLabel,
} from '../../helpers/build-durst-print-day-groups'
import usePrintPlanSummaryEntries from '../../hooks/use-print-plan-summary-entries'
import { buildPrintQueueExecutionOrderConflicts } from '../../helpers/build-print-queue-execution-order-conflicts'
import MachineQueue from './components/machine-queue/machine-queue'
import MachineWaitingQueue from './components/machine-waiting-queue/machine-waiting-queue'
import QueueColumn from './components/queue-column/queue-column'
import { DurstQueueContext } from './durst-queue-context'

const ColumnsRow = styled.div`
    display: flex;
    gap: 24px;
    height: 100%;
    flex-wrap: nowrap;
`

interface MachinesQueuesProps {
    machineBrandName: string
}

const MachinesQueues: FC<MachinesQueuesProps> = ({ machineBrandName }) => {
    const selectedMachineCard =
        PRINT_PLAN_MACHINE_CARDS.find(item => item.key === machineBrandName) ||
        PRINT_PLAN_MACHINE_CARDS[0]
    const { entries } = usePrintPlanSummaryEntries()
    const executionOrderConflicts = useMemo(
        () => buildPrintQueueExecutionOrderConflicts(entries),
        [entries]
    )
    const selectedEntries = filterPrintQueueEntriesByBrands(entries, selectedMachineCard.brandNames)
    const isDurst = selectedMachineCard.key === 'Durst'
    const { data } = useGetMachinesQuery({
        filters: {
            machine_brand: {
                name:
                    selectedMachineCard.brandNames.length > 1
                        ? { $in: selectedMachineCard.brandNames }
                        : { $eq: selectedMachineCard.brandNames[0] },
            },
        },
    })

    const machines = data?.data || []
    const sortedMachines = [...machines].sort((a, b) => a.name.localeCompare(b.name))
    const firstMachine = sortedMachines[0]
    const durstMachineEntries = selectedEntries
        .filter(entry => entry.machine?.documentId === firstMachine?.documentId)
        .sort((a, b) => a.sort - b.sort)
    const durstDayGroups = buildDurstPrintDayGroups(durstMachineEntries)

    if (isDurst) {
        return (
            <DurstQueueContext.Provider
                value={{
                    machineDocId: String(firstMachine?.documentId || ''),
                    dayGroups: durstDayGroups,
                    machineEntries: durstMachineEntries,
                }}
            >
                <ColumnsRow>
                    {(durstDayGroups.length
                        ? durstDayGroups
                        : [
                              {
                                  key: dayjs().format('YYYY-MM-DD'),
                                  plannedPrintDate: dayjs().format('YYYY-MM-DD'),
                                  dayOfWeek: '',
                                  dateLabel: dayjs().format('DD.MM'),
                                  entries: [],
                                  jobsCount: 0,
                                  totalArea: 0,
                                  totalMinutes: 0,
                              },
                          ]
                    ).map((group, index) => (
                        <QueueColumn
                            key={group.key}
                            title={
                                <Typography.Text strong>
                                    {getDurstPrintDayGroupLabel(group.plannedPrintDate)}
                                </Typography.Text>
                            }
                            mainQueue={
                                <MachineQueue
                                    entries={group.entries}
                                    allEntriesForReorder={durstMachineEntries}
                                    executionOrderConflicts={executionOrderConflicts}
                                />
                            }
                            waitingQueue={
                                index === 0 && firstMachine ? (
                                    <MachineWaitingQueue machineDocId={firstMachine.documentId} />
                                ) : undefined
                            }
                        />
                    ))}
                </ColumnsRow>
            </DurstQueueContext.Provider>
        )
    }

    return (
        <ColumnsRow>
            {sortedMachines.map(machine => {
                const machineEntries = selectedEntries.filter(
                    entry => entry.machine?.documentId === machine.documentId
                )
                const totalArea = machineEntries.reduce(
                    (acc, entry) => acc + getPrintQueueEntryAreaM2(entry),
                    0
                )
                const totalMinutes = machineEntries.reduce(
                    (acc, entry) => acc + getPrintQueueEntryMinutes(entry),
                    0
                )

                return (
                    <QueueColumn
                        key={String(machine.documentId)}
                        title={
                            <Flex justify={'space-between'}>
                                <Typography.Text strong>{machine.name}</Typography.Text>

                                <Typography.Text>
                                    {machineEntries.length} зад
                                    <Divider type='vertical' />
                                    {formatNumber(Math.round(totalArea))} м²
                                    <Divider type='vertical' />
                                    {formatDuration(totalMinutes)}
                                </Typography.Text>
                            </Flex>
                        }
                        mainQueue={
                            <MachineQueue
                                machineDocId={machine.documentId}
                                executionOrderConflicts={executionOrderConflicts}
                            />
                        }
                        waitingQueue={<MachineWaitingQueue machineDocId={machine.documentId} />}
                    />
                )
            })}
        </ColumnsRow>
    )
}

export default MachinesQueues
